import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { courtApi } from '../services/api';

const PaymentFailed = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (paymentId) {
      fetchPaymentData();
    } else {
      setError('No payment ID provided');
      setLoading(false);
    }
  }, [paymentId]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const result = await courtApi.getPayment(paymentId);
      setPaymentData(result);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching payment:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleRetryPayment = () => {
    // Navigate back to court details to retry booking
    if (paymentData?.booking?.courtId) {
      // Extract court type from court data or navigate to home
      navigate('/');
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white">
          <div className="w-12 h-12 border-4 border-white border-opacity-30 border-t-white rounded-full animate-spin mb-5"></div>
          <p className="text-xl">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
      <div className="flex flex-col items-center justify-center h-screen text-white text-center">
        {/* Failed Payment Display */}
        <div className="mb-8">
          <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-2">{t('payment_failed')}</h1>
          <p className="text-lg opacity-90 mb-4">{t('payment_was_not_completed')}</p>
          
          {/* Payment Details */}
          {paymentData && (
            <div className="bg-white bg-opacity-20 rounded-2xl p-6 max-w-sm mb-6">
              <h3 className="text-lg font-semibold mb-3">{t('booking_details')}</h3>
              <div className="text-sm space-y-2 text-left">
                <div className="flex justify-between">
                  <span>{t('court')}:</span>
                  <span className="font-medium">{paymentData?.court?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('date')}:</span>
                  <span className="font-medium">{paymentData?.booking?.date}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('time')}:</span>
                  <span className="font-medium">{paymentData?.booking?.startTime} - {paymentData?.booking?.endTime}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('amount')}:</span>
                  <span className="font-medium">฿{paymentData?.payment?.amount}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            onClick={handleRetryPayment}
            className="bg-white bg-opacity-20 border-2 border-white text-white px-6 py-3 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-green-500"
          >
            {t('try_again')}
          </button>
          <button
            onClick={() => navigate('/')}
            className="bg-white bg-opacity-20 border-2 border-white text-white px-6 py-3 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-green-500"
          >
            {t('back_to_home')}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-8 text-sm opacity-75 max-w-md">
          <p>{t('payment_failed_help')}</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailed; 